import { useState, useEffect } from 'react';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

// ==========================================
// CACHE CONFIGURATION
// ==========================================
import { CACHE_DURATIONS } from '../config/cacheConfig';
const CACHE_DURATION = CACHE_DURATIONS.MODELS;

const modelsCache = {
  data: null,
  timestamp: 0,
  params: null
};

/**
 * Hook to load AI models from API - WITH CACHING
 *
 * Usage:
 * const { models, loading, error, refetch } = useModels({ category: 'text' });
 */
export const useModels = (options = {}) => {
  const { category, provider, isActive = true } = options;

  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cacheKey = JSON.stringify({ category, provider, isActive });

  const fetchModels = async (forceRefresh = false) => {
    // Check cache first (only if not forcing refresh)
    if (!forceRefresh && modelsCache.data && modelsCache.params === cacheKey) {
      const cacheAge = Date.now() - modelsCache.timestamp;
      if (cacheAge < CACHE_DURATION) {
        setModels(modelsCache.data);
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      // STATIC MODE: load from local JSON file
      const response = await fetch('/data/models.json');

      if (!response.ok) {
        throw new Error('Failed to load models');
      }

      let data = await response.json();

      // Apply filters locally (same as API used to do)
      if (category) data = data.filter(m => m.category === category);
      if (provider) data = data.filter(m => m.provider === provider);
      if (isActive !== undefined) data = data.filter(m => m.is_active === isActive);

      // Update cache
      modelsCache.data = data;
      modelsCache.timestamp = Date.now();
      modelsCache.params = cacheKey;

      setModels(data);
    } catch (err) {
      console.error('Failed to load models:', err);
      setError(err.message);
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [category, provider, isActive]);

  // Return refetch function for manual refresh
  return {
    models,
    loading,
    error,
    refetch: () => fetchModels(true)
  };
};

/**
 * Hook to get a single model by ID
 */
export const useModel = (modelId) => {
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!modelId) {
      setLoading(false);
      return;
    }

    const fetchModel = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/models/${modelId}`);

        if (!response.ok) {
          throw new Error('Model not found');
        }

        const data = await response.json();
        setModel(data);
      } catch (err) {
        console.error('Failed to load model:', err);
        setError(err.message);
        setModel(null);
      } finally {
        setLoading(false);
      }
    };

    fetchModel();
  }, [modelId]);

  return { model, loading, error };
};

/**
 * Hook for admin model management
 */
export const useModelManagement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Clear cache when models are modified
  const clearCache = () => {
    modelsCache.data = null;
    modelsCache.timestamp = 0;
    modelsCache.params = null;
  };

  const createModel = async (modelData) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/models`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(modelData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create model');
      }

      const data = await response.json();
      clearCache(); // Clear cache after modification
      return { success: true, model: data };
    } catch (err) {
      console.error('Failed to create model:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const updateModel = async (modelId, updates) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/models/${modelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update model');
      }

      const data = await response.json();
      clearCache(); // Clear cache after modification
      return { success: true, model: data };
    } catch (err) {
      console.error('Failed to update model:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteModel = async (modelId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/models/${modelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete model');
      }

      clearCache(); // Clear cache after modification
      return { success: true };
    } catch (err) {
      console.error('Failed to delete model:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const activateModel = async (modelId) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/models/${modelId}/activate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to activate model');
      }

      clearCache(); // Clear cache after modification
      return { success: true };
    } catch (err) {
      console.error('Failed to activate model:', err);
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  return {
    createModel,
    updateModel,
    deleteModel,
    activateModel,
    loading,
    error
  };
};

// Export cache clear function for external use
export const clearModelsCache = () => {
  modelsCache.data = null;
  modelsCache.timestamp = 0;
  modelsCache.params = null;
};